import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AutoResolvedSuggestion, InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { loadAutoResolvedSuggestions } from './loadAutoResolvedSuggestions';
import { AutoResolvedRowState } from '../../enums/autoResolvedRowState';
import {
  useGetAutoResolvedSuggestionsService,
  useSetAutoResolveConfirmationService,
  useUndoAutoResolvedSuggestionService,
} from '../../services/inboxEntries.service';
import { useUnreadStore } from '../../store';
import { useAutoResolvedSuggestionsStore } from '../../store/DoBox/useAutoResolvedSuggestionsStore';
import { useActiveInboxEntriesSlice } from '../InboxEntries/useActiveInboxEntriesSlice';
import { useCustomToast } from '../useCustomToast';

type RunWithBusy = (
  entryId: string,
  task: () => Promise<void>,
) => Promise<void>;

export interface UseAutoResolvedRowActionsResult {
  undo: (item: AutoResolvedSuggestion) => Promise<void>;
  confirm: (item: AutoResolvedSuggestion) => Promise<void>;
  unconfirm: (item: AutoResolvedSuggestion) => Promise<void>;
  busyEntryIds: ReadonlySet<string>;
}

const wasConfirmed = (answer: InboxEntry, resolvedAt: number): boolean =>
  answer.autoResolveConfirmedAt === resolvedAt;

const wasUnconfirmed = (answer: InboxEntry): boolean =>
  answer.autoResolveConfirmedAt == null;

const useRowBusy = () => {
  const [busyEntryIds, setBusyEntryIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const setBusy = useCallback((entryId: string, isBusy: boolean) => {
    setBusyEntryIds((previous) => {
      const next = new Set(previous);
      if (isBusy) next.add(entryId);
      else next.delete(entryId);
      return next;
    });
  }, []);

  const runWithBusy = useCallback<RunWithBusy>(
    async (entryId, task) => {
      setBusy(entryId, true);
      try {
        await task();
      } finally {
        setBusy(entryId, false);
      }
    },
    [setBusy],
  );

  return { busyEntryIds, runWithBusy };
};

const useRefetchAutoResolvedSuggestions = () => {
  const { t } = useTranslation();
  const { getAutoResolvedSuggestions } = useGetAutoResolvedSuggestionsService();
  const { errorMessage } = useCustomToast();

  return useCallback(async () => {
    const groupId = useAutoResolvedSuggestionsStore.getState().taskGroupId;
    if (!groupId) return;
    try {
      await loadAutoResolvedSuggestions(getAutoResolvedSuggestions, groupId);
    } catch {
      errorMessage(t('doBox.autoResolved.loadError'));
    }
  }, [getAutoResolvedSuggestions, errorMessage, t]);
};

const useUndoAction = (
  runWithBusy: RunWithBusy,
  refetch: () => Promise<void>,
) => {
  const { t } = useTranslation();
  const { undoAutoResolvedSuggestion } = useUndoAutoResolvedSuggestionService();
  const { updateInboxEntry } = useActiveInboxEntriesSlice();
  const { markAsRead } = useUnreadStore();
  const { warningMessage, errorMessage } = useCustomToast();

  return useCallback(
    async (item: AutoResolvedSuggestion): Promise<void> => {
      const entryId = item.entry.id;
      const { resolvedAt } = item.entry;
      if (typeof resolvedAt !== 'number') {
        log.error(`Cannot undo ${entryId}: resolvedAt is not a number`);
        return;
      }
      await runWithBusy(entryId, async () => {
        try {
          const answer = await undoAutoResolvedSuggestion(entryId, resolvedAt);
          if (answer?.isSuggestion === true && !answer.resolvedAt) {
            const store = useAutoResolvedSuggestionsStore.getState();
            store.markRow(entryId, AutoResolvedRowState.UNDONE);
            store.markRestored(entryId);
            markAsRead(entryId, answer.taskGroupId);
            updateInboxEntry(answer);
            return;
          }
          warningMessage(t('doBox.autoResolved.alreadyChanged'));
          await refetch();
        } catch (error) {
          log.error('Error undoing auto-resolved suggestion:', error);
          errorMessage(t('doBox.autoResolved.undoError'));
        }
      });
    },
    [
      runWithBusy,
      refetch,
      undoAutoResolvedSuggestion,
      markAsRead,
      updateInboxEntry,
      warningMessage,
      errorMessage,
      t,
    ],
  );
};

const useConfirmationAction = (
  runWithBusy: RunWithBusy,
  refetch: () => Promise<void>,
) => {
  const { t } = useTranslation();
  const { setAutoResolveConfirmation } = useSetAutoResolveConfirmationService();
  const { warningMessage, errorMessage } = useCustomToast();

  return useCallback(
    async (item: AutoResolvedSuggestion, confirmed: boolean): Promise<void> => {
      const entryId = item.entry.id;
      const { resolvedAt } = item.entry;
      if (typeof resolvedAt !== 'number') {
        log.error(`Cannot answer ${entryId}: resolvedAt is not a number`);
        return;
      }
      await runWithBusy(entryId, async () => {
        try {
          const answer = await setAutoResolveConfirmation(
            entryId,
            resolvedAt,
            confirmed,
          );
          const applied =
            !!answer &&
            (confirmed
              ? wasConfirmed(answer, resolvedAt)
              : wasUnconfirmed(answer));
          if (applied) {
            useAutoResolvedSuggestionsStore
              .getState()
              .markRow(
                entryId,
                confirmed
                  ? AutoResolvedRowState.CONFIRMED
                  : AutoResolvedRowState.ACTIVE,
              );
            return;
          }
          warningMessage(t('doBox.autoResolved.alreadyChanged'));
          await refetch();
        } catch (error) {
          log.error('Error answering auto-resolved suggestion:', error);
          errorMessage(t('doBox.autoResolved.confirmError'));
        }
      });
    },
    [
      runWithBusy,
      refetch,
      setAutoResolveConfirmation,
      warningMessage,
      errorMessage,
      t,
    ],
  );
};

// Never rejects: the callers await these straight from a click handler.
export const useAutoResolvedRowActions =
  (): UseAutoResolvedRowActionsResult => {
    const { busyEntryIds, runWithBusy } = useRowBusy();
    const refetch = useRefetchAutoResolvedSuggestions();
    const undo = useUndoAction(runWithBusy, refetch);
    const applyConfirmation = useConfirmationAction(runWithBusy, refetch);

    const confirm = useCallback(
      (item: AutoResolvedSuggestion) => applyConfirmation(item, true),
      [applyConfirmation],
    );
    const unconfirm = useCallback(
      (item: AutoResolvedSuggestion) => applyConfirmation(item, false),
      [applyConfirmation],
    );

    return { undo, confirm, unconfirm, busyEntryIds };
  };
